import { useCallback, useContext, useEffect } from "react";
import {
    GlobalJSBridgePayload,
    GlobalJSBridgeType,
    QuickCheckoutCartApiStatus,
    UpdateQuickCheckoutDataPayload,
} from "@swiggy-private/cart-types";
import {
    CheckoutActionTypes,
    CheckoutContext,
    FEATURE_NR_CALL_STATUS,
    buildNRErrorExtra,
    recordQuickCheckoutJsBridge,
} from "@swiggy-private/checkout-core";
import { captureError } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";

import { parseQuickCheckoutCartUpdateTimestamp } from "@cart-client/helpers/QuickCheckoutCart";

import { useGlobalJSBridgeHandler } from "./useGlobalJSBridgeHandler";

interface UseQuickCheckoutGlobalJSBridgeProps {
    /**
     * Invoked with the validated app payload for each `UPDATE_QUICK_CHECKOUT_FOOTER`
     * bridge trigger. This hook stays transport-only — parsing/validation live here,
     * while status sync and CartContext promotion are owned by the caller.
     */
    onQuickCheckoutUpdate: (data: UpdateQuickCheckoutDataPayload) => void;
    /**
     * Invoked when a SUCCESS trigger carries no cart at all. The payload is still rejected
     * (no ack, so the app can retry), but the caller needs to move the lifecycle out of the
     * previous status — otherwise an in-progress update stays on screen forever.
     */
    onSuccessWithoutCart?: () => void;
}

export const useQuickCheckoutGlobalJSBridge = ({
    onQuickCheckoutUpdate,
    onSuccessWithoutCart,
}: UseQuickCheckoutGlobalJSBridgeProps) => {
    const { checkoutState } = useContext(CheckoutContext);
    const { registerCallbackForType, unregisterCallbackForType } = useGlobalJSBridgeHandler();

    const handleQuickCheckoutCartUpdate = useCallback(
        (payload: GlobalJSBridgePayload) => {
            try {
                const data = payload?.data as UpdateQuickCheckoutDataPayload | undefined;
                // Reject while on MRO/full cart so native update-cart bridge payloads cannot
                // mutate QC state (or ack) outside the quick-checkout screen.
                if (checkoutState.type !== CheckoutActionTypes.SHOW_QUICK_CHECKOUT) {
                    recordQuickCheckoutJsBridge({
                        status: FEATURE_NR_CALL_STATUS.REJECTED,
                        reason: "not_on_quick_checkout",
                        ack: false,
                    });
                    return false;
                }
                if (isEmpty(data) || isEmpty(data?.cart_update_status)) {
                    recordQuickCheckoutJsBridge({
                        status: FEATURE_NR_CALL_STATUS.REJECTED,
                        reason: "empty_payload",
                        ack: false,
                    });
                    return false;
                }
                // A cart + timestamp are only expected on success, so requiring them here
                // would drop the loading trigger the footer needs for its in-progress state.
                // Don't use isEmpty(last_updated_timestamp) — numeric timestamps are "empty" for lodash-like isEmpty.
                if (data.cart_update_status === QuickCheckoutCartApiStatus.SUCCESS) {
                    if (isEmpty(data.cart_response)) {
                        captureError(new Error("QuickCheckout bridge SUCCESS rejected: missing cart_response"));
                        recordQuickCheckoutJsBridge({
                            status: FEATURE_NR_CALL_STATUS.REJECTED,
                            cartUpdateStatus: data.cart_update_status,
                            reason: "missing_cart_response",
                            ack: false,
                        });
                        onSuccessWithoutCart?.();
                        return false;
                    }
                    if (parseQuickCheckoutCartUpdateTimestamp(data.last_updated_timestamp) === null) {
                        captureError(
                            new Error(
                                "QuickCheckout bridge SUCCESS rejected: missing or invalid last_updated_timestamp",
                            ),
                        );
                        recordQuickCheckoutJsBridge({
                            status: FEATURE_NR_CALL_STATUS.REJECTED,
                            cartUpdateStatus: data.cart_update_status,
                            reason: "missing_last_updated_timestamp",
                            ack: false,
                        });
                        return false;
                    }
                }
                onQuickCheckoutUpdate(data);
                recordQuickCheckoutJsBridge({
                    status: FEATURE_NR_CALL_STATUS.SUCCESS,
                    cartUpdateStatus: data.cart_update_status,
                    ack: true,
                });
                return true;
            } catch (err) {
                recordQuickCheckoutJsBridge({
                    status: FEATURE_NR_CALL_STATUS.FAILURE,
                    reason: "bridge_handler_exception",
                    ack: false,
                    extra: buildNRErrorExtra(err),
                });
                return false;
            }
        },
        [checkoutState.type, onQuickCheckoutUpdate, onSuccessWithoutCart],
    );

    const cleanupQuickCheckoutGlobalJSBridge = useCallback(() => {
        unregisterCallbackForType(GlobalJSBridgeType.UPDATE_QUICK_CHECKOUT_FOOTER, () => {
            // returning false to give no ack to apps once cleanup is done so they can continue polling
            return false;
        });
    }, [unregisterCallbackForType]);

    useEffect(() => {
        registerCallbackForType(GlobalJSBridgeType.UPDATE_QUICK_CHECKOUT_FOOTER, handleQuickCheckoutCartUpdate);
        return () => {
            cleanupQuickCheckoutGlobalJSBridge();
        };
    }, [cleanupQuickCheckoutGlobalJSBridge, handleQuickCheckoutCartUpdate, registerCallbackForType]);
};
